import { Link, useSearchParams } from "react-router";
import { listCollection } from "../lib/content.server.js";
import { breadcrumbJsonLd, buildMeta } from "../lib/seo.js";
import Breadcrumbs from "../components/Breadcrumbs/Breadcrumbs.jsx";
import Picture from "../components/Picture/Picture.jsx";
import FilterChips from "../components/FilterChips/FilterChips.jsx";
import CTABand from "../components/CTABand/CTABand.jsx";
import { surfaceFor } from "../lib/surface.js";
import styles from "./case-studies.module.css";

const PAGE = {
  title: "Case Studies",
  description:
    "How DOTs and agencies used Road Safety Audit 3D and 3D Visualization to find hazards early, win public support and deliver on time.",
  path: "/case-studies",
};
const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Case Studies", path: PAGE.path },
];
const OPTIONS = [
  { value: "all", label: "All" },
  { value: "rsa3d", label: "Road Safety Audit 3D" },
  { value: "123bim", label: "3D Visualization" },
];
const PRODUCT = {
  rsa3d: "Road Safety Audit 3D",
  "123bim": "3D Visualization",
  both: "RSA 3D and 3D Visualization",
};

// The one switch for this page's light sections: these sit on the light "plan sheet"
// surface from app/styles/surfaces.css. Empty the list to put the whole page back on the
// dark surface; nothing else needs to change.
const LIGHT_SECTIONS = ["studies"];
const surface = surfaceFor(LIGHT_SECTIONS);

export function meta() {
  return buildMeta({ ...PAGE, jsonLd: [breadcrumbJsonLd(CRUMBS)] });
}

export async function loader() {
  // Newest first.
  return { items: listCollection("case-studies") };
}

const matches = (e, product) => product === "all" || e.product === product || e.product === "both";

/**
 * One case study as a row: its picture beside the result it produced. The title links to
 * the study and the whole row is its target. A study without a headline figure
 * (`highlight`) shows its summary in that place.
 */
function Study({ study }) {
  return (
    <article className={styles.study}>
      <Picture
        src={study.image}
        alt={study.imageAlt || ""}
        widths={[480, 960, 1600]}
        fallbackWidth={960}
        sizes="(min-width: 64rem) 40vw, 100vw"
        className={styles.image}
      />
      <div className={styles.body}>
        <p className={styles.meta}>
          <span className={styles.product}>{PRODUCT[study.product] || study.product}</span>
          <span>{study.client}</span>
          {study.date ? <span>{study.date.slice(0, 4)}</span> : null}
        </p>
        <h2 className={styles.title}>
          <Link to={`/case-studies/${study.slug}`} className={styles.link}>
            {study.title}
          </Link>
        </h2>
        {study.highlight ? (
          <p className={styles.result}>
            <strong>{study.highlight}</strong> {study.highlightText}
          </p>
        ) : null}
        <p className={styles.summary}>{study.summary || study.description}</p>
        <p className={styles.more} aria-hidden="true">
          Read the case study
        </p>
      </div>
    </article>
  );
}

export default function CaseStudies({ loaderData }) {
  const { items: all } = loaderData;
  const [params, setParams] = useSearchParams();
  const product = OPTIONS.some((o) => o.value === params.get("product"))
    ? params.get("product")
    : "all";
  const items = all.filter((e) => matches(e, product));
  const options = OPTIONS.map((o) => ({
    ...o,
    count: all.filter((e) => matches(e, o.value)).length,
  }));
  return (
    <>
      <section className={styles.hero} aria-labelledby="case-studies-title">
        <div className="container">
          <Breadcrumbs items={CRUMBS} />
          <h1 id="case-studies-title" className={styles.heroTitle}>
            How DOTs find hazards early and win public support
          </h1>
          <p className={styles.heroLead}>
            Projects from state DOTs and the firms they work with, on Road Safety Audit 3D and 3D
            Visualization, each with what it changed.
          </p>
        </div>
      </section>

      <section className="section" data-surface={surface("studies")} aria-label="Case studies">
        <div className="container">
          <FilterChips
            label="Filter by product"
            options={options}
            value={product}
            onChange={(v) => setParams(v === "all" ? {} : { product: v }, { replace: true })}
          />
          {items.length ? (
            <ol className={styles.list}>
              {items.map((e) => (
                <li key={e.slug}>
                  <Study study={e} />
                </li>
              ))}
            </ol>
          ) : (
            <p className={styles.empty}>No case studies for this product yet.</p>
          )}
        </div>
      </section>

      <CTABand
        variant="statement"
        image="/images/case-studies/udot-rsa-3d/hero"
        title="Your project could be the next case study"
        text="Start with one corridor or one design, and judge it on your own project."
        secondary={{ to: "/contact", label: "Talk to our team" }}
      />
    </>
  );
}
